// Stock Opname — Phase 1D-δ-2. Multi-step wizard + list.
// State machine: DRAFT (create) → IN_PROGRESS (start) → items input → FINALIZED (auto-adjust)
//
// ⛔ Kode state DIUKUR, bukan disalin. Versi lama baris ini menulis `COUNTING`,
// kode yang nol pernah ada di sisi mana pun kecuali berkas ini — lihat catatan
// panjang di `INV_OPNAME_STATUS_TONE` (OPNAME-COUNTING-01). Sumbernya
// 010a_phase1d_inventory_core.sql:208 dan inventory_service.py:590.
//
// Pola daftar seragam (28 Sep 2026; `docs/POLA_DAFTAR.md`):
//   * Riwayat sesi = mode SERVER penuh, keadaan di alamat, saringan bawaan "Belum selesai" (draf +
//     sedang dihitung) + gudang sendiri (Admin Gudang). Dialog menambah `?create`/`?wizard` ke alamat.
//   * LEMBAR HITUNG = LENGKAP, dari SERVER (`GET /inventory/opname/{id}/lembar-hitung`): seluruh bahan
//     aktif + bahan yang sudah punya baris di sesi ini, jumlah sistem, dan hasil hitung. Dulu browser
//     memindai master bahan sendiri (`ambilSemua`) — berhenti dengan galat di 5.000 bahan, dan dua kali
//     pernah memotong diam-diam. Halaman lembar (100 baris) hanya MEMBAGI tampilan; seluruh bahan tetap
//     ada di memori, isian tidak hilang saat pindah halaman, dan hitungan "belum dihitung" atas SEMUA bahan.
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { ClipboardCheck, Plus, RefreshCw, ChevronRight, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "../../lib/format";
import { OpnameStatusBadge, INV_OPNAME_STATUS_TONE } from "../../components/etams/StatusBadge";
import {
  listOpname, createOpname, getOpname, getOpnameLembar, startOpname, upsertOpnameItems,
  finalizeOpname, cancelOpname,
  apiErrorMessage,
} from "../../lib/inventoryApi";
import { mintaTeks } from "../../lib/dialogAplikasi";
import { tanggalLokalISO } from "../../lib/format";
import Hint from "../../components/etams/Hint";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";

const LABEL_SARING = { BELUM_SELESAI: "Belum selesai", DRAFT: "Draf", IN_PROGRESS: "Sedang Dihitung", FINALIZED: "Selesai", CANCELLED: "Dibatalkan" };
//: Cermin `URUT_SAH_OPNAME` di backend/services/inventory_service.py.
const URUT_SAH = ["opname_date", "opname_number", "status", "created_at"];
const KOLOM_CSV = [
  { kunci: "opname_number", judul: "Nomor" },
  { kunci: "store", judul: "Gudang", ambil: (o) => o.store?.name || "" },
  { kunci: "opname_date", judul: "Tanggal Opname" },
  { kunci: "status", judul: "Status", ambil: (o) => LABEL_SARING[o.status] || o.status },
  { kunci: "created_at", judul: "Dibuat", ambil: (o) => formatDateTime(o.created_at) },
];

/** Tambah/hapus kunci dialog di alamat TANPA membuang keadaan daftar (h, q, saringan). */
function ubahParamDialog(setSp, ubah) {
  setSp((prev) => {
    const n = new URLSearchParams(prev);
    ["create", "wizard"].forEach((k) => n.delete(k));
    Object.entries(ubah || {}).forEach(([k, v]) => n.set(k, v));
    return n;
  }, { replace: true });
}

// KONTRAS-01 Batch 9 — peta `STATUS_BADGE` lokal yang dulu di sini (4 status
// dengan `cls:` warna keras) pindah ke `INV_OPNAME_STATUS_TONE` di
// `components/etams/StatusBadge.jsx`. Label tidak berubah sepotong pun;
// `CANCELLED` bergeser dari MERAH ke netral — alasannya di berkas itu.


const LS_KEY = (id) => `opname-draft-${id}`;

export default function Opname() {
  const { roleCode, profile } = useAuth();
  const [sp, setSp] = useSearchParams();
  const wizardId = sp.get("wizard");
  const showCreate = sp.get("create") === "1";

  const canWrite = ["OWNER", "KEPALA_CABANG", "ADMIN_GUDANG"].includes(roleCode);
  const gudangSaya = (roleCode === "ADMIN_GUDANG" && profile?.store_id) || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/stores").then(r => setStores(r.data || []))
      .catch((e) => toast.error(apiErrorMessage(e, "Daftar gudang gagal dimuat.")));
  }, []);

  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "opname_date", arah: "desc" },
    saringan: {
      status: { label: "Status", param: "status", bawaan: "BELUM_SELESAI", tampil: (v) => LABEL_SARING[v] || v },
      gudang: { label: "Gudang", param: "store_id", bawaan: gudangSaya,
                tampil: (v) => stores.find(x => x.id === v)?.name || "…" },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const bangunParams = useCallback((o = {}) => ({
    ...paramSaringan, ...(cari && { q: cari }), limit, offset, sort, arah, ...o,
  }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await listOpname(bangunParams({ offset: off, limit: n }))).data, [bangunParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listOpname(bangunParams());
      setItems(r.data.items || []);
      setTotal(r.data.total ?? 0);
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal memuat opname.")); }
    finally { setLoading(false); }
  }, [bangunParams]);

  useEffect(() => { load(); }, [load]);
  const tutupWizard = useCallback(() => { ubahParamDialog(setSp, {}); load(); }, [setSp, load]);

  return (
    <div className="space-y-6" data-testid="opname-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6" /> Stock Opname (Bahan Baku)
          </h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
            Sesi hitung fisik bahan baku.
            <Hint>Alurnya: Draf → Sedang Dihitung → isi hasil hitung → Selesai. Tiap selisih hitungan fisik dengan catatan sistem otomatis jadi draf koreksi stok.</Hint>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-1" /> Muat ulang
          </Button>
          {canWrite && (
            <Button size="sm" onClick={() => ubahParamDialog(setSp, { create: "1" })} data-testid="opname-create-btn">
              <Plus className="h-4 w-4 mr-1" /> Sesi Baru
            </Button>
          )}
        </div>
      </div>

      <PalangDaftar daftar={daftar} placeholder="Cari nomor sesi / catatan" testId="opname-palang"
                    ekspor={{ nama: "sesi_opname", kolom: KOLOM_CSV, ambilHalaman }}>
        <select value={daftar.nilaiSaringan.status} onChange={(e) => daftar.setSaringan("status", e.target.value)}
                className="h-9 rounded border px-2 text-sm" data-testid="opname-status-filter" aria-label="Saring status">
          <option value="">Semua status</option>
          {Object.entries(LABEL_SARING).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={daftar.nilaiSaringan.gudang} onChange={(e) => daftar.setSaringan("gudang", e.target.value)}
                className="h-9 rounded border px-2 text-sm" data-testid="opname-store-filter" aria-label="Saring gudang">
          <option value="">Semua Gudang</option>
          {stores.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
      </PalangDaftar>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr className="text-left">
                <ThUrut daftar={daftar} kunci="opname_number" className="px-4 py-3 font-medium">Nomor</ThUrut>
                <th className="px-4 py-3 font-medium">Gudang</th>
                <ThUrut daftar={daftar} kunci="opname_date" className="px-4 py-3 font-medium">Tanggal Opname</ThUrut>
                <ThUrut daftar={daftar} kunci="status" className="px-4 py-3 font-medium">Status</ThUrut>
                <ThUrut daftar={daftar} kunci="created_at" className="px-4 py-3 font-medium">Dibuat</ThUrut>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading && daftar.tampil.length === 0 && (
                <tr><td colSpan="6" className="p-8 text-center text-muted-foreground">Memuat...</td></tr>
              )}
              {!loading && daftar.tampil.length === 0 && (
                <tr><td colSpan="6" className="p-12 text-center text-muted-foreground text-sm" data-testid="opname-kosong">
                  {daftar.cari.trim() || daftar.saringanAktif.length
                    ? "Tidak ada sesi opname yang cocok dengan pencarian atau saringan ini."
                    : `Belum ada sesi opname.${canWrite ? " Klik 'Sesi Baru' untuk mulai." : ""}`}
                </td></tr>
              )}
              {daftar.tampil.map(o => (
                <tr key={o.id} className="border-b hover:bg-muted/50">
                  <td className="px-4 py-3 font-mono text-xs">{o.opname_number || "—"}</td>
                  <td className="px-4 py-3">{o.store?.name || "—"}</td>
                  <td className="px-4 py-3">{o.opname_date || "-"}</td>
                  <td className="px-4 py-3"><OpnameStatusBadge value={o.status} /></td>
                  <td className="px-4 py-3 text-xs">{formatDateTime(o.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => ubahParamDialog(setSp, { wizard: o.id })}
                            data-testid={`opname-open-${o.id}`}>Buka</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Paginasi daftar={daftar} testId="opname-paginasi" />
      </Card>

      {showCreate && <CreateOpnameDialog onClose={(id) => { ubahParamDialog(setSp, id ? { wizard: id } : {}); if (!id) load(); }} />}
      {wizardId && <OpnameWizard id={wizardId} onClose={tutupWizard} />}
    </div>
  );
}

// -----------------------------------------------------------------------
// Create sesi (Step 1)
// -----------------------------------------------------------------------
function CreateOpnameDialog({ onClose }) {
  const [stores, setStores] = useState([]);
  const [form, setForm] = useState({ store_id: "", opname_date: tanggalLokalISO(), notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/stores").then(r => setStores(r.data || [])); }, []);

  const submit = async () => {
    if (!form.store_id) { toast.error("Cabang wajib dipilih."); return; }
    setSaving(true);
    try {
      const r = await createOpname(form);
      toast.success("Sesi opname dibuat. Lanjut ke langkah berikutnya.");
      onClose(r.data.id);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(null)}>
      <DialogContent data-testid="opname-create-dialog">
        <DialogHeader><DialogTitle>Sesi Opname Baru — Langkah 1: Persiapan</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm">Cabang / Gudang *</span>
            <select value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })}
                    className="w-full h-9 rounded border px-2 text-sm" data-testid="opname-store">
              <option value="">— Pilih —</option>
              {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-sm">Tanggal Opname</span>
            <Input type="date" value={form.opname_date}
                   onChange={(e) => setForm({ ...form, opname_date: e.target.value })} />
          </label>
          <label className="block">
            <span className="text-sm">Catatan / PIC</span>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      rows={2} placeholder="PIC opname, tim, catatan sesi..." />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onClose(null)}>Batal</Button>
          <Button onClick={submit} disabled={saving} data-testid="opname-create-submit">
            {saving ? "Membuat..." : "Buat & Buka Wizard"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------
// Wizard multi-step (Steps 2-4)
// -----------------------------------------------------------------------
function OpnameWizard({ id, onClose }) {
  const [op, setOp] = useState(null);
  const [step, setStep] = useState(2);  // 2=input, 3=preview, 4=submit
  // Lembar hitung LENGKAP dari server: [{material_id, code, name, unit_of_measure, qty_sistem, qty_counted, notes}]
  const [lembar, setLembar] = useState([]);
  const [counts, setCounts] = useState({}); // material_id → { qty_counted, notes }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, l] = await Promise.all([getOpname(id), getOpnameLembar(id)]);
      setOp(r.data);
      const baris = l.data?.items || [];
      setLembar(baris);
      // Hitungan yang sudah tersimpan di server (lembar = SELURUH baris sesi, tanpa plafon).
      const initCounts = {};
      baris.forEach(it => {
        if (it.qty_counted !== null && it.qty_counted !== undefined) {
          initCounts[it.material_id] = { qty_counted: it.qty_counted, notes: it.notes || "" };
        }
      });
      // Merge with localStorage draft
      const draft = JSON.parse(localStorage.getItem(LS_KEY(id)) || "{}");
      Object.keys(draft).forEach(k => {
        if (!(k in initCounts)) initCounts[k] = draft[k];
      });
      setCounts(initCounts);
    } catch (e) { toast.error(apiErrorMessage(e)); onClose(); }
  }, [id, onClose]);

  useEffect(() => { load(); }, [load]);

  // ⛔ DULU efek di sini memindai master bahan (`ambilSemua(listMaterials)`) + saldo gudang di BROWSER.
  //    Sejarahnya: plafon 200 membuat 59 dari 259 bahan TIDAK PERNAH dihitung (5 Sep 2026); pindaian
  //    penggantinya berhenti dengan galat di 5.000 bahan. Kini lembar hitung dibangun SERVER, lengkap,
  //    tanpa plafon (`lembar_hitung_opname`, dibuktikan >1.000 dan >5.000 di test_inventori_daftar_murni).
  const perBahan = useMemo(() => new Map(lembar.map((x) => [x.material_id, x])), [lembar]);
  const qtySistem = (mid) => Number(perBahan.get(mid)?.qty_sistem || 0);
  // Dibagi per halaman HANYA untuk tampilan (seluruh baris ada di memori; isian disimpan per bahan).
  const daftarLembar = useDaftar(lembar, {
    url: false, ukuran: 100, kunciCari: ["code", "name"], kolom: [{ kunci: "code" }, { kunci: "name" }],
    urutAwal: { kunci: "code", arah: "asc" },
  });

  // Persist counts draft to localStorage
  useEffect(() => {
    if (Object.keys(counts).length > 0) {
      localStorage.setItem(LS_KEY(id), JSON.stringify(counts));
    }
  }, [counts, id]);

  const setCount = (mid, patch) => setCounts({
    // Bawaan "" bukan 0 — kotak kosong berarti BELUM DIHITUNG.
    ...counts, [mid]: { ...(counts[mid] || { qty_counted: "", notes: "" }), ...patch },
  });

  const doStart = async () => {
    setBusy(true);
    try { await startOpname(id); toast.success("Sesi dimulai — silakan isi hasil hitung."); await load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const doSaveItems = async () => {
    const rows = Object.entries(counts)
      .filter(([_, c]) => c.qty_counted !== "" && c.qty_counted !== null)
      .map(([mid, c]) => ({
        material_id: mid, qty_counted: Number(c.qty_counted), notes: c.notes || null,
      }));
    if (rows.length === 0) { toast.error("Minimal 1 item hitungan wajib."); return; }
    setBusy(true);
    try {
      await upsertOpnameItems(id, { items: rows });
      toast.success(`${rows.length} item tersimpan.`);
      await load();
      setStep(3);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const doFinalize = async () => {
    setBusy(true);
    try {
      const r = await finalizeOpname(id);
      toast.success("Sesi opname selesai. Koreksi stok otomatis dibuat untuk setiap selisih.");
      localStorage.removeItem(LS_KEY(id));
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const doCancelSession = async () => {
    const reason = await mintaTeks("Alasan membatalkan sesi opname:", { minPanjang: 5 });
    if (!reason || reason.trim().length < 5) return;
    setBusy(true);
    try {
      await cancelOpname(id, { reason: reason.trim() });
      toast.success("Sesi dibatalkan.");
      localStorage.removeItem(LS_KEY(id));
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  if (!op) return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent><div className="p-8 text-center text-muted-foreground">Memuat...</div></DialogContent>
    </Dialog>
  );

  // Label saja — badge-nya dirender `OpnameStatusBadge`. Ditarik dari peta
  // tone supaya prosa baris "Sesi opname ini sudah ..." dan badge tidak bisa
  // berbunyi beda.
  const st = INV_OPNAME_STATUS_TONE[op.status] || { l: op.status };
  const isDraft = op.status === "DRAFT";
  // ⛔ JANGAN "perbaiki" `IN_PROGRESS` kembali jadi `COUNTING`. Nama variabelnya
  // menyebut keadaan BISNIS ("sedang menghitung"); yang dibandingkan adalah KODE
  // BASIS DATA, dan keduanya memang tidak seejaan. Sampai OPNAME-COUNTING-01
  // baris ini membandingkan ke `COUNTING` — kode yang nol pernah ditulis backend
  // — sehingga `isCounting` PERMANEN FALSE dan kedua gerbang di bawahnya
  // (`step === 2` dan `step === 3`) nol pernah merender. Layar ini praktis tidak
  // bisa dipakai lewat Draft, dan nol test menangkapnya karena nol test frontend.
  const isCounting = op.status === "IN_PROGRESS";
  const isDone = ["FINALIZED", "CANCELLED"].includes(op.status);

  // Pratinjau selisih — HANYA untuk bahan yang SUDAH dihitung.
  //
  // ⛔ Dulu baris ini berbunyi `Number(c.qty_counted || 0)`, dan `|| 0`
  // menjadikan "belum diisi" tak terbedakan dari "diisi nol". Diukur lewat
  // layar 29 Agu 2026: satu bahan diisi tangan, pratinjau berbunyi
  // "Selisih != 0: 46" — 45 bahan tak tersentuh diusulkan DIHAPUS.
  //
  // Backend sudah dibetulkan (qty_counted NULL = belum dihitung). Tanpa
  // baris ini ikut dibetulkan, layar akan tetap BERBOHONG walau datanya
  // sudah benar — dan yang dilihat staf adalah layar, bukan tabel.
  const sudahDiisi = (v) => v !== "" && v !== null && v !== undefined;
  const previewDelta = Object.entries(counts)
    .filter(([_, c]) => sudahDiisi(c.qty_counted))
    .map(([mid, c]) => {
      const sys = qtySistem(mid);
      const counted = Number(c.qty_counted);
      return { mid, sys, counted, delta: Math.round((counted - sys) * 1000) / 1000 };
    }).filter(x => !isNaN(x.counted));

  // Bahan yang SENGAJA nol disentuh. Ditampilkan sebagai keterangan, bukan
  // disembunyikan: staf perlu tahu berapa yang belum ia hitung SEBELUM
  // menekan Selesaikan.
  // Dihitung atas SELURUH lembar (bukan hanya bahan yang pernah disentuh).
  const belumDihitung = lembar.filter((b) => !sudahDiisi(counts[b.material_id]?.qty_counted)).length;

  const matLabel = (mid) => {
    const m = perBahan.get(mid);
    return m ? `${m.code} — ${m.name}` : "—";
  };

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="opname-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Opname {op.opname_number || "—"} <OpnameStatusBadge value={op.status} />
          </DialogTitle>
        </DialogHeader>

        {/* Step breadcrumb */}
        <div className="flex items-center gap-2 text-xs mb-2">
          <span className={step >= 1 ? "font-semibold" : "text-muted-foreground"}>1. Persiapan</span>
          <ChevronRight className="h-3 w-3 text-muted-foreground" />
          <span className={step >= 2 ? "font-semibold" : "text-muted-foreground"}>2. Isi Hitungan</span>
          <ChevronRight className="h-3 w-3 text-muted-foreground" />
          <span className={step >= 3 ? "font-semibold" : "text-muted-foreground"}>3. Lihat Selisih</span>
          <ChevronRight className="h-3 w-3 text-muted-foreground" />
          <span className={step >= 4 ? "font-semibold" : "text-muted-foreground"}>4. Selesaikan</span>
        </div>

        {isDone && (
          <div className="bg-muted/50 border rounded p-4 text-sm">
            Sesi opname ini sudah <strong>{st.l}</strong>. Datanya tidak bisa diubah lagi.
          </div>
        )}

        {isDraft && (
          <div className="bg-warning-soft text-warning-soft-foreground border border-warning/40 rounded p-3 text-sm space-y-2">
            <div>Sesi masih <strong>Draf</strong>. Klik "Mulai Sesi" untuk mengunci jumlah versi sistem dan mulai menghitung.</div>
            <Button size="sm" onClick={doStart} disabled={busy} data-testid="opname-start">
              Mulai Sesi (Kunci Jumlah Sistem)
            </Button>
          </div>
        )}

        {isCounting && step === 2 && (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              Isi hasil hitungan fisik per bahan — {lembar.length} bahan di lembar ini, {belumDihitung} belum dihitung.
              <Hint>Isian tersimpan otomatis di perangkat ini. Pindah halaman tidak menghapus isian.</Hint>
            </div>
            <PalangDaftar daftar={daftarLembar} placeholder="Cari kode / nama bahan" testId="opname-lembar-palang" />
            <div className="overflow-x-auto max-h-96 overflow-y-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b sticky top-0">
                  <tr className="text-left">
                    <ThUrut daftar={daftarLembar} kunci="code" className="px-3 py-2 font-medium">Bahan</ThUrut>
                    <th className="px-3 py-2 font-medium text-right">Qty Sistem</th>
                    <th className="px-3 py-2 font-medium text-right">Qty Fisik</th>
                    <th className="px-3 py-2 font-medium">Catatan</th>
                  </tr>
                </thead>
                <tbody>
                  {daftarLembar.tampil.map(b => {
                    const m = { id: b.material_id, code: b.code, name: b.name };
                    const sys = Number(b.qty_sistem || 0);
                    const c = counts[m.id] || {};
                    return (
                      <tr key={m.id} className="border-b" data-testid={`opname-baris-${m.id}`}>
                        <td className="px-3 py-2"><span className="font-mono text-xs">{m.code}</span> — {m.name}</td>
                        <td className="px-3 py-2 text-right text-muted-foreground">{sys}</td>
                        <td className="px-3 py-2 text-right">
                          <Input type="number" step="0.001" value={c.qty_counted ?? ""}
                                 onChange={(e) => setCount(m.id, { qty_counted: e.target.value })}
                                 className="h-8 text-right" data-testid={`opname-qty-${m.id}`} />
                        </td>
                        <td className="px-3 py-2">
                          <Input value={c.notes ?? ""}
                                 onChange={(e) => setCount(m.id, { notes: e.target.value })}
                                 className="h-8" placeholder="opsional" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Paginasi daftar={daftarLembar} testId="opname-lembar-paginasi" />
            <div className="flex justify-between">
              <Button variant="outline" onClick={doCancelSession} disabled={busy}>Batalkan Sesi</Button>
              <Button onClick={doSaveItems} disabled={busy} data-testid="opname-save-next">
                Simpan & Lanjut ke Selisih <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {isCounting && step === 3 && (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Selisih antara hitungan fisik dan catatan sistem. Klik "Selesaikan"
              untuk otomatis membuat koreksi stok bagi tiap bahan yang selisihnya
              bukan nol.
            </div>
            {/* Bahan yang belum dihitung DISEBUT, bukan disembunyikan. Staf
                perlu tahu berapa yang ia lewatkan SEBELUM menekan Selesaikan —
                dan perlu tahu bahwa melewatkannya AMAN. */}
            {belumDihitung > 0 && (
              <div className="text-sm rounded border border-info/40 bg-info-soft
                              text-info-soft-foreground p-3">
                <strong>{belumDihitung} bahan belum dihitung</strong> dan
                sengaja tidak muncul di daftar ini — stoknya{" "}
                <strong>tidak akan diubah</strong>. Kosongkan kalau memang belum
                sempat dihitung; isi <strong>0</strong> hanya kalau bahannya
                sudah dicek dan memang habis.
              </div>
            )}
            <div className="overflow-x-auto max-h-96 overflow-y-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b sticky top-0">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium">Bahan</th>
                    <th className="px-3 py-2 font-medium text-right">Qty Sistem</th>
                    <th className="px-3 py-2 font-medium text-right">Qty Fisik</th>
                    <th className="px-3 py-2 font-medium text-right">Selisih</th>
                  </tr>
                </thead>
                <tbody>
                  {previewDelta.map(p => (
                    <tr key={p.mid} className="border-b">
                      <td className="px-3 py-2">{matLabel(p.mid)}</td>
                      <td className="px-3 py-2 text-right text-muted-foreground">{p.sys}</td>
                      <td className="px-3 py-2 text-right font-semibold">{p.counted}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${p.delta === 0 ? "text-muted-foreground" : p.delta < 0 ? "text-danger-soft-foreground" : "text-success-soft-foreground"}`}>
                        {p.delta > 0 ? "+" : ""}{p.delta}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-muted-foreground">
              Total item: {previewDelta.length} • Selisih ≠ 0: {previewDelta.filter(p => p.delta !== 0).length}
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ChevronLeft className="h-4 w-4 mr-1" /> Kembali ke Hitungan
              </Button>
              <Button onClick={doFinalize} disabled={busy}
                      data-testid="opname-finalize">
                Selesaikan (Buat Koreksi Stok)
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
